/**
 * Pilcore PMS — Public Booking Engine
 * Cancel booking screen (public)
 *
 * Notes:
 * - This screen must NOT load Stripe.
 * - Reads token params once, then cleans URL via history.replaceState (no localStorage).
 * - DB remains source of truth; frontend only calls public API.
 *
 * Language:
 * - Reads `lang` from querystring (?lang=es|en). Defaults to EN.
 * - lang is kept even after URL cleaning (state only); refresh would lose it (same as token).
 */

import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  applyBookingThemeToRoot,
  normalizeBookingTheme,
} from "./bookingThemes";
import "./bookingThemes.css";

export default function BookingCancel() {
  const { slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const isMobile =
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    (window.matchMedia("(pointer: coarse)").matches ||
      window.matchMedia("(max-width: 768px)").matches);

  const qs = useMemo(() => new URLSearchParams(String(location?.search || "")), [location?.search]);

  // ✅ language from URL (?lang=es|en) — read immediately (before cleaning URL)
  const initialLang = useMemo(() => {
    const raw = String(qs.get("lang") || "").trim().toLowerCase();
    return raw.startsWith("es") ? "es" : "en";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // only once

  const [lang] = useState(initialLang);

  const I18N = useMemo(
    () => ({
      en: {
        title: "CANCEL RESERVATION",
        hotel: "Hotel:",
        booking_reference: "Booking reference:",
        loading: "Loading…",
        warning: "You are about to cancel your reservation. This action may not be reversible.",
        success: "Your reservation has been cancelled.",
        invalid_link: "Invalid or incomplete cancellation link. Please use the link from your email again.",
        cancel_btn: "CANCEL BOOKING",
        cancelling_btn: "CANCELLING...",
        back_btn: "BACK TO BOOKING",
        expired_tip: "If the cancellation link has expired, please use the most recent email you received.",
        ctx_error: "Unable to load booking context.",
        cancel_error: "Unable to cancel this booking.",
      },
      es: {
        title: "CANCELAR RESERVA",
        hotel: "Hotel:",
        booking_reference: "Referencia de reserva:",
        loading: "Cargando…",
        warning: "Vas a cancelar tu reserva. Esta acción puede no ser reversible.",
        success: "Tu reserva ha sido cancelada.",
        invalid_link: "Enlace de cancelación inválido o incompleto. Vuelve a abrir el enlace desde el email.",
        cancel_btn: "CANCELAR RESERVA",
        cancelling_btn: "CANCELANDO...",
        back_btn: "VOLVER A RESERVAR",
        expired_tip: "Si el enlace de cancelación ha caducado, utiliza el email más reciente que recibiste.",
        ctx_error: "No se pudo cargar el contexto de reserva.",
        cancel_error: "No se pudo cancelar esta reserva.",
      },
    }),
    []
  );

  const t = (k) => {
    const pack = I18N[lang] || I18N.en;
    return pack[k] || I18N.en[k] || k;
  };

  const [token, setToken] = useState({
    group_locator: "",
    token: "", // ✅ DB one-time token
    exp: "",
    sig: "",
    });

  const [context, setContext] = useState(null);
  const bookingTheme = normalizeBookingTheme(context?.booking_engine_theme);
  const [loadingCtx, setLoadingCtx] = useState(false);

  useEffect(
    () => applyBookingThemeToRoot(bookingTheme),
    [bookingTheme]
  );

  const [cancelling, setCancelling] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  // 1) Read params once and clean URL (to avoid "scary" querystring)
  useEffect(() => {
    const group_locator = String(qs.get("group_locator") || qs.get("group") || "").trim();

    // ✅ NEW: DB token
    const dbToken = String(qs.get("token") || "").trim();

    // legacy fallback
    const exp = String(qs.get("exp") || "").trim();
    const sig = String(qs.get("sig") || "").trim();

    //console.log("[BOOKING][CANCEL][TOKEN][READ]", {
        //slug,
        //lang,
        //has_group_locator: !!group_locator,
        //has_db_token: !!dbToken,
        //has_exp: !!exp,
        //has_sig: !!sig,
    //});

    setToken({ group_locator, token: dbToken, exp, sig });

    const cleanPath = `/booking/${encodeURIComponent(String(slug || "").trim())}/cancel`;
    if (String(location?.search || "")) {
        try {
        window.history.replaceState({}, "", cleanPath);
        //console.log("[BOOKING][CANCEL][URL][CLEANED]", { cleanPath });
        } catch (e) {
        //console.warn("[BOOKING][CANCEL][URL][CLEAN_FAIL]", { message: e?.message });
        }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slug]);


  // 2) Load booking context (hotel name, etc.)
  useEffect(() => {
    if (!slug) return;

    setLoadingCtx(true);
    setErr("");

    (async () => {
      try {
        //console.log("[BOOKING][CANCEL][CONTEXT][LOAD][START]", { slug });
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/public/booking/context/${encodeURIComponent(slug)}`
        );
        setContext(res?.data || null);
        //console.log("[BOOKING][CANCEL][CONTEXT][LOAD][OK]", {
          //slug,
          //keys: Object.keys(res?.data || {}),
        //});
      } catch (e) {
        //console.error("[BOOKING][CANCEL][CONTEXT][LOAD][ERROR]", {
          //message: e?.message,
          //status: e?.response?.status,
          //data: e?.response?.data,
        //});
        setErr(e?.response?.data?.error || t("ctx_error"));
        setContext(null);
      } finally {
        setLoadingCtx(false);
      }
    })();
  }, [slug]); // keep as-is (no need to refetch on lang)

  async function handleCancel() {
    setErr("");

    const group_locator = String(token.group_locator || "").trim();

        // ✅ NEW: DB one-time token (si viene en el link)
        const dbToken = String(token.token || "").trim();

        // legacy fallback
        const exp = String(token.exp || "").trim();
        const sig = String(token.sig || "").trim();

        const hasDbToken = !!dbToken;

        // ✅ validation: (dbToken) OR (exp+sig)
        if (!slug || !group_locator || (!hasDbToken && (!exp || !sig))) {
        setErr(t("invalid_link"));
        //console.warn("[BOOKING][CANCEL][VALIDATION][FAIL]", {
            //slug,
            //group_locator,
            //hasDbToken,
            //exp_present: !!exp,
            //sig_present: !!sig,
        //});
        return;
        }

        try {
        setCancelling(true);

        //console.log("[BOOKING][CANCEL][POST][START]", {
            //slug,
            //group_locator,
            //hasDbToken,
            //exp: hasDbToken ? null : exp,
        //});

        const url = `${process.env.REACT_APP_API_URL}/api/public/booking/cancel-group/${encodeURIComponent(
            String(slug || "").trim()
        )}`;

        const body = {
            group_locator,
            cancellation_reason:
            lang === "es"
                ? "Cancelado por el huésped (Booking Engine)"
                : "Cancelled by guest (Booking Engine)",
            updated_by: "PublicBookingCancel",
        };

        // ✅ if DB token exists -> send it; else fallback to exp/sig
        if (hasDbToken) {
            body.token = dbToken;
        } else {
            body.exp = exp;
            body.sig = sig;
        }

        const resp = await axios.post(url, body);

        //console.log("[BOOKING][CANCEL][POST][OK]", resp?.data);
        setDone(true);
        } catch (e) {
        //console.error("[BOOKING][CANCEL][POST][ERROR]", {
            //message: e?.message,
            //status: e?.response?.status,
            //data: e?.response?.data,
        //});
        setErr(e?.response?.data?.error || t("cancel_error"));
        setDone(false);
        } finally {
        setCancelling(false);
        }

  }

  const hotelName =
    String(context?.booking_engine_profile?.public_hotel_name || "").trim() ||
    String(context?.hotel_name || "").trim() ||
    "Hotel";

  return (
    <div
      className="bookingCancelPage container-fluid min-vh-100"
      data-booking-theme={bookingTheme}
      style={{
        backgroundColor: "#000",
        minHeight: "100dvh",
        paddingBottom: "calc(18px + env(safe-area-inset-bottom))",
      }}
    >
      <div style={{ maxWidth: "920px", margin: "0 auto", padding: isMobile ? "16px 12px" : "22px 14px" }}>
        <div
          className="bookingCancelCard"
          style={{
            border: "1px solid rgba(255,255,255,0.10)",
            background: "rgba(255,255,255,0.03)",
            borderRadius: "14px",
            padding: isMobile ? "14px" : "16px",
            boxSizing: "border-box",
          }}
        >
          <div
            style={{
              fontWeight: 900,
              letterSpacing: isMobile ? "0.10em" : "0.14em",
              marginBottom: "10px",
              fontSize: isMobile ? "0.95rem" : "1rem",
              lineHeight: 1.2,
              textAlign: isMobile ? "center" : "left",
            }}
          >
            {t("title")}
          </div>

          <div
            style={{
              color: "rgba(255,255,255,0.75)",
              lineHeight: 1.45,
              fontSize: isMobile ? "0.95rem" : "1rem",
            }}
          >
            <div style={{ marginBottom: "6px" }}>
              <span style={{ color: "rgba(255,255,255,0.55)" }}>{t("hotel")}</span> {hotelName}
            </div>

            <div style={{ marginBottom: "6px" }}>
              <span style={{ color: "rgba(255,255,255,0.55)" }}>{t("booking_reference")}</span>{" "}
              {String(token.group_locator || "").trim() || "—"}
            </div>

            {loadingCtx && (
              <div style={{ marginTop: "10px", color: "rgba(255,255,255,0.55)" }}>
                {t("loading")}
              </div>
            )}

            {!done && (
              <div style={{ marginTop: "12px", color: "rgba(255,255,255,0.65)" }}>
                {t("warning")}
              </div>
            )}

            {done && (
              <div className="bookingSuccess" role="status" style={{ marginTop: "12px", color: "#9ff0b4", fontWeight: 900 }}>
                {t("success")}
              </div>
            )}

            {!!err && <div className="bookingError" role="alert" style={{ marginTop: "12px", color: "#ffb3b3" }}>{err}</div>}

            <div
              style={{
                display: "flex",
                gap: "12px",
                marginTop: "16px",
                flexDirection: isMobile ? "column" : "row",
                alignItems: "stretch",
              }}
            >
              <button
                className="bookingSecondaryAction"
                type="button"
                onClick={() => {
                  //console.log("[BOOKING][CANCEL][BACK_TO_BOOKING]", { slug });
                  navigate(`/booking/${encodeURIComponent(String(slug || "").trim())}`);
                }}
                style={{
                  flex: 1,
                  padding: isMobile ? "14px 12px" : "12px",
                  background: "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(255,255,255,0.12)",
                  color: "#fff",
                  fontWeight: 900,
                  letterSpacing: isMobile ? "0.08em" : "0.12em",
                  fontSize: isMobile ? "0.92rem" : "0.95rem",
                  cursor: "pointer",
                }}
              >
                {t("back_btn")}
              </button>

              {!done && (
                <button
                  className="bookingDangerAction"
                  type="button"
                  onClick={handleCancel}
                  disabled={cancelling}
                  style={{
                    flex: 1,
                    padding: isMobile ? "14px 12px" : "12px",
                    background: cancelling ? "rgba(255,255,255,0.10)" : "#c84b4b",
                    border: "none",
                    color: "#fff",
                    fontWeight: 900,
                    letterSpacing: isMobile ? "0.08em" : "0.12em",
                    fontSize: isMobile ? "0.92rem" : "0.95rem",
                    opacity: cancelling ? 0.75 : 1,
                    cursor: cancelling ? "not-allowed" : "pointer",
                  }}
                >
                  {cancelling ? t("cancelling_btn") : t("cancel_btn")}
                </button>
              )}
            </div>

            <div style={{ marginTop: "12px", color: "rgba(255,255,255,0.45)", fontSize: "0.85rem" }}>
              {t("expired_tip")}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
